import {
  type GitlabProvisioningManageResult,
  type GitlabProvisioningManageStatusResponse,
} from 'network/httpClients/GitlabClient';

/** Per-scope statuses that count as a success (created / removed / skipped). */
export const SUCCEEDED_ITEM_STATUSES = new Set<
  GitlabProvisioningManageResult['status']
>(['created', 'removed', 'skipped']);

/**
 * The derived outcome of a manage run, computed from the poll status + per-scope
 * results (never the top-level status alone): `pending` while non-terminal,
 * `success` when all succeeded, `partial` when some failed, `failed` when none
 * succeeded or the run/transport failed.
 */
export type ManageRunOutcome = 'pending' | 'success' | 'partial' | 'failed';

/** Results whose per-scope status is a success (created / removed / skipped). */
export const getSucceededResults = (
  results: GitlabProvisioningManageResult[],
): GitlabProvisioningManageResult[] =>
  results.filter((result) => SUCCEEDED_ITEM_STATUSES.has(result.status));

/** Results whose per-scope status is `failed`. */
export const getFailedResults = (
  results: GitlabProvisioningManageResult[],
): GitlabProvisioningManageResult[] =>
  results.filter((result) => result.status === 'failed');

/**
 * Whether the poll status is terminal (`done` / `failed`) — polling has stopped
 * and the results list is final.
 * @param status - The manage-status poll response (may be undefined pre-first-poll).
 * @returns True when the run has reached a terminal state.
 */
export const isTerminalManageStatus = (
  status: GitlabProvisioningManageStatusResponse | undefined,
): boolean => status?.status === 'done' || status?.status === 'failed';

/**
 * Compute the run outcome from the (optional) transport error, the poll status,
 * and its per-scope results. A transport/start error is always `failed`; a
 * non-terminal status is `pending`; otherwise the split of succeeded vs failed
 * scopes decides success / partial / failed.
 * @param transportErrorKey - A start/transport error key, if the run couldn't be polled.
 * @param status - The manage-status poll response.
 * @returns The derived outcome.
 */
export const computeManageOutcome = (
  transportErrorKey: string | undefined,
  status: GitlabProvisioningManageStatusResponse | undefined,
): ManageRunOutcome => {
  if (transportErrorKey !== undefined) return 'failed';

  if (!isTerminalManageStatus(status)) return 'pending';

  const results = status?.results ?? [];
  const succeeded = getSucceededResults(results);
  const failed = getFailedResults(results);

  if (failed.length === 0 && succeeded.length > 0) return 'success';

  return succeeded.length === 0 ? 'failed' : 'partial';
};

/**
 * Aggregate a cloud multi-group install's per-group outcomes into one overall outcome
 * (drives the top banner + "Done" gate). `pending` while any group is still running;
 * `success` when every group fully succeeded; `failed` when none did; `partial`
 * otherwise (some groups succeeded/partially-succeeded, some failed). A group whose
 * provisioning failed reports `failed`.
 * @param outcomes - One outcome per selected top-level group (empty → pending).
 * @returns The aggregated run outcome.
 */
export const computeMultiGroupOutcome = (
  outcomes: ManageRunOutcome[],
): ManageRunOutcome => {
  if (outcomes.length === 0) return 'pending';

  if (outcomes.some((outcome) => outcome === 'pending')) return 'pending';

  const anySucceeded = outcomes.some(
    (outcome) => outcome === 'success' || outcome === 'partial',
  );

  if (!anySucceeded) return 'failed';

  return outcomes.every((outcome) => outcome === 'success')
    ? 'success'
    : 'partial';
};

/**
 * Display label for a result row. Transient (queued/in_progress) rows carry only
 * the numeric id in `scope` (the server resolves the path lazily), so resolve it
 * from the loaded id→path maps; terminal rows already carry the path.
 * @param result - The per-scope manage result.
 * @param groupPathsById - Loaded group id → fullPath.
 * @param projectPathsById - Loaded project id → pathWithNamespace.
 * @returns The path when resolvable, else the raw scope.
 */
export const getResultLabel = (
  result: GitlabProvisioningManageResult,
  groupPathsById: Map<number, string>,
  projectPathsById: Map<number, string>,
): string => {
  if (!/^\d+$/.test(result.scope)) return result.scope;

  const id = Number(result.scope);
  const paths = result.kind === 'group' ? groupPathsById : projectPathsById;

  return paths.get(id) ?? result.scope;
};

/**
 * Tailwind text-color class for a per-scope result row: positive for
 * created/removed, negative for failed, active for in_progress, muted otherwise.
 * @param result - The per-scope manage result.
 * @returns The text-color utility class.
 */
export const getResultStatusClass = (
  result: GitlabProvisioningManageResult,
): string => {
  if (result.status === 'created' || result.status === 'removed') {
    return 'text-text-tag-positive-active';
  }

  if (result.status === 'failed') return 'text-text-tag-negative-active';

  if (result.status === 'in_progress') {
    return 'text-text-general-neutral-strong';
  }

  return 'text-text-general-neutral-subtle';
};
